import React, { useEffect, useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Tooltip
} from '@chakra-ui/react';
import { cmxDateTime, objectUtils } from '@codametrix/ui-common';
import { styles } from './field-editorv2-styles';
import { theme } from '@codametrix/themes/chakra/biome';
import { CMXIcon } from '@codametrix/ui-components';
import './styles/index.scss';
const { isEmpty } = objectUtils;
/**
 * Fetches the initial label for a field based on the cell key, case edits, and value.
 * For 'billing_location' fields, attempts to find a matching label from mapped values.
 * This is not the ideal way to do this, adding support for CDMX-1832
 * Component itself should be able to manage label - value combination in the dropdown
 *
 * @param {string} cellKey - The key identifying the cell/field
 * @param {CMxCommonApp.CaseEditsState} caseEdits - The current case edits state
 * @param {string} value - The current value of the field
 * @returns {string} The label to display, or the original value if no mapping is found
 */
const fetchInitialLabel = (
  cellKey: string,
  caseEdits: CMxCommonApp.CaseEditsState,
  value: string
) => {
  if (
    cellKey === 'billing_location' &&
    !isEmpty(caseEdits) &&
    Object.keys(caseEdits).includes(cellKey)
  ) {
    const { mappedValues } = caseEdits?.[cellKey];
    const label = mappedValues?.find(
      (map: { label: string; value: string }) => {
        return map?.value === value;
      }
    )?.label;
    return label || value;
  }
  return value;
};

export type FieldEditorProps = {
  cellKey: string;
  label: string;
  value: React.ReactNode | string;
  fieldType?: string;
  onChange: (val: any) => void;
  onSubmit: (val: any) => void;
  fieldEditorInfo: CMxAPI.FieldEditorInfo;
} & React.HTMLAttributes<HTMLDivElement>;

const FieldEditorv2: React.FC<FieldEditorProps> = (props: FieldEditorProps) => {
  const defaultClassname = 'cmx-col-4 cmx-col-md-12';
  const {
    cellKey,
    label,
    value,
    fieldType,
    onChange,
    onSubmit,
    fieldEditorInfo,
    className = defaultClassname,
    ...spreadable
  } = props;
  const classes = classNames(
    `${cellKey}-editor`,
    'field-editorv2',
    'encounter-info',
    className
  );

  const { caseEdits } = fieldEditorInfo;

  let updatedValue = value;
  const [selected, setSelected] = useState(
    fetchInitialLabel(cellKey, caseEdits, value.toString())
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  if (caseEdits && Object.keys(caseEdits).includes(cellKey)) {
    const { newValue, initialValue } = caseEdits[cellKey];

    updatedValue = newValue?.length > 0 ? newValue : initialValue ?? 'N/A';
    // setSelected(updatedValue.toString());
  }

  if (fieldType === 'date' && updatedValue !== 'N/A') {
    updatedValue = cmxDateTime.format(
      updatedValue.toString(),
      cmxDateTime.FORMATS.DATE
    );
  }

  React.useEffect(() => {
    setSelected(fetchInitialLabel(cellKey, caseEdits, value.toString()));
  }, [value]);

  const handleSelect = useCallback(
    (option: string) => {
      setSelected(option);
      onChange(option);
      setIsMenuOpen(false);
    },
    [onChange]
  );

  // Render menu items only when the menu is open
  const renderMenuItems = useMemo(() => {
    if (
      fieldType === 'dictionary' &&
      caseEdits &&
      caseEdits[cellKey] &&
      isMenuOpen
    ) {
      const { options } = caseEdits[cellKey];
      return options.map((option: string, index) => (
        <MenuItem
          sx={option === selected ? styles.itemSelected : styles.item}
          key={`${cellKey}-${option}-${index}`}
          onSelect={() => handleSelect(option)}
          onClick={() => handleSelect(option)}
          value={option}>
          {option}
        </MenuItem>
      ));
    }
    return null;
  }, [fieldType, caseEdits, selected, handleSelect, cellKey, isMenuOpen]);

  return (
    //@ts-ignore
    <div {...spreadable} className={classes} data-testid="field-editor">
      {fieldType === 'date' && (
        <div
          className="datepicker-qa date-field-editor"
          data-testid="field-editor-datepicker">
          <DatePicker
            key={updatedValue.toString()}
            selected={
              selected !== 'N/A' || null || undefined
                ? new Date(`${selected} 00:00`)
                : null
            }
            placeholderText={'No Date Selected'}
            onChange={(e: Date | null) => {
              const newTime = e ? e.toISOString().split('T')[0] : 'N/A';
              setSelected(newTime);
              onChange(newTime);
            }}
          />
        </div>
      )}
      {fieldType === 'dictionary' && (
        <div
          className="dictionary-menu-button"
          data-tooltip={selected}
          data-testid="field-editor-dictionary">
            <Menu
              onOpen={() => setIsMenuOpen(true)}
              onClose={() => setIsMenuOpen(false)}>
              <Tooltip label={selected} placement="top">
                <MenuButton
                  as={Button}
                  sx={styles.button}
                  //key={updatedValue.toString()}
                  rightIcon={
                    <CMXIcon
                      svgIconName="BiomeChevronDownSVG20"
                      svgIconSize="20"
                      svgIconClass="select-checvron"
                    />
                  }
                  _focus={{ boxShadow: 'none' }}
                  _expanded={{
                    bg: theme.colors.text['light'],
                    border: `${theme.borders['1px']} ${theme.colors.foundation[500]}`
                  }}
                  _hover={{
                    border: `${theme.borders['1px']} ${theme.colors.foundation[400]}`,
                    bg: theme.colors.text['light']
                  }}>
                  {selected !== 'N/A' ? `${selected}` : 'No Selection'}
                </MenuButton>
              </Tooltip>

              <MenuList
                sx={styles.list}
                style={{ overflow: 'auto' }}
                width="200px">
                {isMenuOpen && renderMenuItems}
              </MenuList>
            </Menu>
        </div>
      )}
    </div>
  );
};
FieldEditorv2.displayName = 'FieldEditorv2';

export { FieldEditorv2 };
